import { computed, ref } from 'vue';
import type { Ref } from 'vue';
import mitt, { type Emitter } from 'mitt';

import { MessageType } from './enum';
import { deleteMessage, findMessageIndex, hasMessage, randomId, sortMessageItems } from './lib';
import type { MessageListItem, RequiredMessage } from './types';

export interface MessageApi {
  messages: Ref<MessageListItem[]>;
  deleteItem: (message: MessageListItem) => void;
  /**
   * Strictly check before pushing message, fail if any encounters occur. If you want to
   * use a safe version of the following, use, `addItems`. Most of the time, you should use this
   */
  pushItems: (...messages: (RequiredMessage & MessageListItem)[]) => void;
  /**
   * Add one or many messages safely into the list. If there are auto fixable issues, it, will
   * add those props automatically.
   * If a message with the same id is specified, then the original message is removed.
   */
  addItems: (...messages: MessageListItem[]) => MessageListItem[];
  /**
   * Access a message by index. Provides a type cast version of `array.at`. You can access
   * the array location from negative value.
   */
  at: <C extends MessageListItem = MessageListItem>(location: number) => C;
  /**
   * Adds given list of messages at the location, or if the location is null or out of bounds of the array length,
   * then it's added at the end of the message list.
   */
  addItemsAt: (loc: number | null, ...messages: MessageListItem[]) => MessageListItem[];
  /**
   * Delete the list of items from the the given message list item.
   */
  deleteAllMessagesFrom(item: MessageListItem): void;
  /**
   * Clear all the messages from the conversation.
   * @param confirmation Double check if this is intended.
   */
  clear(confirmation: { __CLEARING_CONVERSATION: true }): void;
  /**
   * An event handler to listen on various message events.
   * @deprecated - watch the messages ref instead.
   */
  events: Omit<Emitter<MessageListEvents>, 'emit' | 'all'>;
}

type MessageListEvents = {
  delete: MessageListItem;
  replace: { added: MessageListItem[]; removed: MessageListItem[] };
};

/**
 * Create an intractable list of Message lists
 */
export function useMessages(initial: MessageListItem[] = []): MessageApi {
  const messagesList = ref<MessageListItem[]>(initial);
  const messages = computed(() => sortMessageItems(messagesList.value));
  const events = mitt<MessageListEvents>();

  function deleteItem(message: MessageListItem) {
    const currentMessages = messagesList.value;
    const idx = findMessageIndex(currentMessages, message);
    if (idx >= 0) {
      const item = currentMessages[idx];
      messagesList.value = deleteMessage(messagesList.value, message);
      events.emit('delete', item);
    }
  }

  function deleteAllMessagesFrom(message: MessageListItem) {
    const currentMessages = messagesList.value;
    const idx = findMessageIndex(currentMessages, message);

    if (idx < 0) return;

    messagesList.value = currentMessages.slice(0, idx);
  }

  function pushItems(...messages: (RequiredMessage & MessageListItem)[]) {
    messages.forEach((message) => {
      if (!message.id) throw 'Every message needs an id';
      if (hasMessage(messagesList.value, message)) throw 'Message already exists or a message with same id exists';
    });
    if (messages.length) {
      messagesList.value.push(...messages);
      events.emit('replace', { added: messages, removed: [] });
    }
  }

  function addItems(...messages: MessageListItem[]) {
    const remove: number[] = [];
    const currentMessages = messagesList.value;
    const cleanMessages = messages.map((m) => {
      if (!m.type) m.type = MessageType.DEFAULT;
      if (!m.id) m.id = randomId();

      const existingMessageIdx = findMessageIndex(messagesList.value, m);
      if (existingMessageIdx >= 0) remove.push(existingMessageIdx);

      return m;
    });

    const itemsToRemove = remove.map((idx) => currentMessages[idx]);

    messagesList.value = [...currentMessages.filter((_, idx) => !remove.includes(idx)), ...cleanMessages];

    events.emit('replace', { added: cleanMessages, removed: itemsToRemove });

    return messagesList.value;
  }

  function addAt(loc: number | null, ...messages: MessageListItem[]) {
    if (typeof loc !== 'number') return addItems(...messages);

    let list: MessageListItem[] = [...messagesList.value];
    const itemsToRemove: MessageListItem[] = list.filter((m) => findMessageIndex(messages, m) !== -1);
    list.splice(loc, 0, ...messages);

    if (itemsToRemove.length) {
      list = list.filter((m) => itemsToRemove.indexOf(m) === -1);
    }
    messagesList.value = list;
    return messagesList.value;
  }

  function at<C extends MessageListItem = MessageListItem>(location: number): C {
    const list = messagesList.value;
    if (location >= 0) return list[location] as C;
    return list[list.length + location] as C;
  }

  function clear({ __CLEARING_CONVERSATION }: { __CLEARING_CONVERSATION: true }) {
    if (!__CLEARING_CONVERSATION) return;
    deleteAllMessagesFrom(at(0));
  }

  return {
    messages,
    deleteItem,
    pushItems,
    addItems,
    at,
    addItemsAt: addAt,
    events,
    deleteAllMessagesFrom,
    clear,
  };
}
